import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import {
  mdiAccountBoxOutline,
  mdiApi,
  mdiBell,
  mdiConnection,
  mdiHelpCircle,
  mdiTruckOutline
} from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";

import { IconProps } from "@mdi/react/dist/IconProps";
import {
  AutoThemeSVGIcon,
  BestPriceSVGIcon,
  BigTruckCheckSVGIcon,
  BigTruckCogSVGIcon,
  BrokerPostingSVGIcon,
  BusinessDocSVGIcon,
  DarkModeSVGIcon,
  DarkPhoneThemeSVGIcon,
  DirectShippingSVGIcon,
  LightPhoneThemeSVGIcon,
  ReceiptLongSVGIcon
} from "components/SVGIcons";
import { formatNumber } from "helpers/formatNumber";
import { IOption } from "models/Form";
import { ITableHeaderList } from "models/HaulPay";
import { IUserSettingItem } from "models/UserSetting";
import { routes } from "./routes";

export const USER_SETTINGS_LIST: IUserSettingItem[] = [
  {
    id: 0,
    title: "Notifications",
    description: "Manage notifications",
    iconHeader: (props: FunctionComponent<IconProps>) => <Icon path={mdiBell} {...props} />,
    link: routes.SETTINGS_NOTIFICATIONS
  },
  {
    id: 1,
    title: "Profile",
    description: "Manage profile, account payable & receivable info",
    iconHeader: (props: FunctionComponent<IconProps>) => (
      <Icon path={mdiAccountBoxOutline} {...props} />
    ),
    link: routes.SETTINGS_PROFILE
  },
  // {
  //   id: 2,
  //   title: "Bank Account",
  //   description: "Manage your linked bank accounts",
  //   iconHeader: (props: FunctionComponent<IconProps>) => <Icon path={mdiBankOutline} {...props} />,
  //   link: routes.SETTINGS_BANK_ACCOUNT
  // },
  {
    id: 3,
    title: "Billing",
    description: "HaulPay factoring users have full access for free",
    iconHeader: (props: React.SVGProps<SVGSVGElement>) => <ReceiptLongSVGIcon {...props} />,
    link: routes.SETTINGS_BILLING
  },
  {
    id: 4,
    title: "Theme",
    description: "Switch between Dark & Light Themes",
    iconHeader: (props: React.SVGProps<SVGSVGElement>) => <DarkModeSVGIcon {...props} />,
    link: routes.SETTINGS_THEME
  },
  {
    id: 5,
    title: "Help",
    description: "Contact us or read general FAQ’s",
    iconHeader: (props: FunctionComponent<IconProps>) => <Icon path={mdiHelpCircle} {...props} />,
    link: routes.SETTINGS_HELP
  }
];

export const INTEGRATIONS_SETTINGS_ITEM: IUserSettingItem = {
  id: 6,
  title: "Integrations",
  description: "Connect your TMS to HaulPay",
  iconHeader: (props: FunctionComponent<IconProps>) => <Icon path={mdiConnection} {...props} />,
  link: routes.SETTINGS_INTEGRATIONS
};

export const ALVYS_SETTINGS_ITEM: IUserSettingItem = {
  id: 7,
  title: "Alvys",
  description: "Send your invoiced Alvys loads to HaulPay",
  iconHeader: (props: FunctionComponent<IconProps>) => <Icon path={mdiTruckOutline} {...props} />,
  link: routes.SETTINGS_INTEGRATIONS_ALVYS
};

export const API_SETTINGS_ITEM: IUserSettingItem = {
  id: 8,
  title: "API",
  description: "Get your HaulPay API token",
  iconHeader: (props: FunctionComponent<IconProps>) => <Icon path={mdiApi} {...props} />,
  link: routes.SETTINGS_INTEGRATIONS_API
};

export const ALERT_FREQUENCY_LIST: IOption[] = [
  {
    id: 0,
    label: "Real Time",
    value: "real"
  },
  {
    id: 1,
    label: "Every 2 hours (default)",
    value: "two"
  },
  {
    id: 2,
    label: "Every 4 hours",
    value: "four"
  }
];

export const THEMES = [
  {
    id: 0,
    subtitle: "Dark Theme",
    value: "dark",
    phoneTheme: (props: React.SVGProps<SVGSVGElement>) => <DarkPhoneThemeSVGIcon {...props} />
  },
  {
    id: 1,
    subtitle: "Light Theme",
    value: "light",
    phoneTheme: (props: React.SVGProps<SVGSVGElement>) => <LightPhoneThemeSVGIcon {...props} />
  },
  {
    id: 2,
    subtitle: "Automatic",
    value: "auto",
    phoneTheme: (props: React.SVGProps<SVGSVGElement>) => <AutoThemeSVGIcon {...props} />
  }
];

export const BILLING_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "created",
    label: "Transaction Date",
    render: (item: number) => <span>{dayjs.unix(item).format("MM/DD/YYYY h:mm:ss A")}</span>
  },
  {
    value: "amount",
    label: "Amount",
    render: (item: number) => <span>{formatNumber(item / 100, 2)}</span>
  },
  {
    value: "receipt",
    label: "Receipt",
    render: (item: { action: VoidFunction }) => (
      <button className="underline text-info" onClick={item.action}>
        View Receipt
      </button>
    )
  }
];

export const PRICING_PLAN_BENEFITS = [
  {
    id: 0,
    icon: (props: React.SVGProps<SVGSVGElement>) => <DirectShippingSVGIcon {...props} />,
    content: "Full access to bid and/or message all direct shipper loads"
  },
  {
    id: 1,
    icon: (props: React.SVGProps<SVGSVGElement>) => <BrokerPostingSVGIcon {...props} />,
    content: "Full access to bid and/or message all broker posted loads"
  },
  {
    id: 2,
    icon: (props: React.SVGProps<SVGSVGElement>) => <BigTruckCheckSVGIcon {...props} />,
    content: "Unlimited load searching, truck posting and load matching"
  },
  {
    id: 3,
    icon: (props: React.SVGProps<SVGSVGElement>) => <BigTruckCogSVGIcon {...props} />,
    content: "Get unrestricted full access to new shipper load alerts"
  },
  {
    id: 4,
    icon: (props: React.SVGProps<SVGSVGElement>) => <BusinessDocSVGIcon {...props} />,
    content: "Store and send important business documents via cloud"
  },
  {
    id: 5,
    icon: (props: React.SVGProps<SVGSVGElement>) => <BestPriceSVGIcon {...props} />,
    content: "Market trending rates ON loads and unlimited use of rate trends"
  }
];

export const PAYMENT_PENDING_BOARDING_HEADER: ITableHeaderList[] = [
  {
    value: "group_data.group_name",
    label: "Broker"
  },
  {
    value: "group_data.company_email",
    label: "Email"
  },
  {
    value: "group_data.company_phone_number",
    label: "Phone"
  },
  {
    value: "group_data.company_mc",
    label: "MC #"
  },
  {
    value: "group_data.company_dot",
    label: "DOT #"
  },
  {
    value: "token",
    label: "Boarding",
    render: (item: string) => {
      return (
        <Link
          to={`/invited/${item}`}
          className="px-8 py-2 text-center rounded-base bg-primary w-fit">
          Next
        </Link>
      );
    }
  }
];

export const PAYMENT_CONNECTED_BOARDING_HEADER = (): ITableHeaderList[] => [
  {
    value: "group_data.group_name",
    label: "Broker"
  },
  {
    value: "group_data.company_email",
    label: "Email"
  },
  {
    value: "group_data.company_phone_number",
    label: "Phone #"
  },
  {
    value: "group_data.company_mc",
    label: "MC #"
  },
  {
    value: "group_data.company_dot",
    label: "DOT #"
  },
  {
    value: "paymentSpeed",
    label: "Payment Speed",
    // HP-8422: a plain label rather than a permanently disabled dropdown — the carrier cannot
    // change this here, the page tells them to contact the broker.
    render: (item: string | undefined) => <span>{item ?? "—"}</span>
  }
];
